import PbEnhancedElement from "../pb_enhanced_element";
import { PbDropdownKeyboard } from "./keyboard_accessibility";
import { setArrowVisibility, toggleVisibility } from "../utilities/domHelpers";
import { subscribeFloatingKitReposition, positionDropdownPortalToWrapper } from "../utilities/floatingPortalHosts";
import { getCreationOption, matchesOptionExactly } from "./utilities/optionCreation";
import { setQuickPickDateOnPicker } from "./utilities/quickpickDateSync";
import { bindPillReorder } from "./utilities/pillReorder";

const DROPDOWN_SELECTOR = "[data-pb-dropdown]";
const TRIGGER_SELECTOR = "[data-dropdown-trigger]";
const CONTAINER_SELECTOR = "[data-dropdown-container]";
const DOWN_ARROW_SELECTOR = "[data-dropdown-open-icon]";
const UP_ARROW_SELECTOR = "[data-dropdown-close-icon]";
const OPTION_SELECTOR = "[data-dropdown-option-label]";
const CUSTOM_DISPLAY_SELECTOR = "[data-dropdown-custom-trigger]";
const DROPDOWN_TRIGGER_DISPLAY = "[data-dropdown-trigger-display]";
const DROPDOWN_PLACEHOLDER = "[data-dropdown-placeholder]";
const DROPDOWN_INPUT = "[data-dropdown-selected-option]";
const SEARCH_INPUT_SELECTOR = "[data-dropdown-autocomplete]";
const SEARCH_BAR_SELECTOR = "[data-dropdown-search]";
const CLEAR_ICON_SELECTOR = "[data-dropdown-clear-icon]";
const LABEL_SELECTOR = '[data-dropdown="pb-dropdown-label"]';

// Portal host + positioning (parity with utilities/floatingPortalHosts.ts + React Dropdown).
const PB_DIALOG_FLOATING_ROOT = "[data-pb-dialog-floating-root]";
const PB_FLOATING_OWNER_ATTR = "data-pb-floating-owner";

function resolveFloatingOwnerId(fromElement) {
  if (!fromElement) return null;
  const marked = fromElement.closest(`[${PB_FLOATING_OWNER_ATTR}]`);
  if (marked) return marked.getAttribute(PB_FLOATING_OWNER_ATTR);
  const popoverEl = fromElement.closest(".pb_popover_tooltip");
  if (popoverEl?.id) return popoverEl.id;
  const dialogEl = fromElement.closest("dialog, .pb_dialog");
  if (dialogEl?.id) return dialogEl.id;
  return null;
}

const DIALOG_DIRECT_FLOATING_ROOT = `:scope > ${PB_DIALOG_FLOATING_ROOT}`;
const POPOVER_FLOATING_ROOT = `:scope > .pb_popover_body > ${PB_DIALOG_FLOATING_ROOT}`;

function resolveDialogFloatingPortalHost(fromElement) {
  if (!fromElement) return null;
  const dialogEl = fromElement.closest("dialog");
  if (dialogEl) {
    return dialogEl.querySelector(DIALOG_DIRECT_FLOATING_ROOT) || dialogEl;
  }
  const reactModalShell = fromElement.closest(".pb_dialog");
  if (reactModalShell) {
    return (
      reactModalShell.querySelector(DIALOG_DIRECT_FLOATING_ROOT) ||
      reactModalShell
    );
  }
  const popoverEl = fromElement.closest(".pb_popover_tooltip");
  if (popoverEl) {
    return (
      popoverEl.querySelector(POPOVER_FLOATING_ROOT) ||
      popoverEl.querySelector(PB_DIALOG_FLOATING_ROOT) ||
      popoverEl
    );
  }
  return null;
}

function resolvePortaledKitHost(kitRoot, dialogCtxTarget) {
  if (typeof document === "undefined") return null;
  if (kitRoot && kitRoot.closest(".pb_popover_tooltip")) {
    return document.body;
  }
  return (
    resolveDialogFloatingPortalHost(kitRoot) || dialogCtxTarget || null
  );
}

function clearDropdownPortalPanelStyles(panel) {
  [
    "position",
    "left",
    "top",
    "right",
    "bottom",
    "width",
    "z-index",
    "margin",
  ].forEach((prop) => {
    panel.style.removeProperty(prop);
  });
}

export default class PbDropdown extends PbEnhancedElement {
  static get selector() {
    return DROPDOWN_SELECTOR;
  }

  get target() {
    return this.cachedElements?.target || this.element.querySelector(CONTAINER_SELECTOR);
  }

  get baseInput() {
    return this.cachedElements?.baseInput || this.element.querySelector(DROPDOWN_INPUT);
  }

  get trigger() {
    return this.cachedElements?.trigger || this.element.querySelector(TRIGGER_SELECTOR);
  }

  get customTrigger() {
    return this.cachedElements?.customTrigger || this.element.querySelector(CUSTOM_DISPLAY_SELECTOR);
  }

  get dropdownWrapper() {
    return this.cachedElements?.dropdownWrapper || this.element.querySelector(".dropdown_wrapper");
  }

  get placeholder() {
    return this.cachedElements?.placeholder || this.element.querySelector(DROPDOWN_PLACEHOLDER);
  }

  cacheElements() {
    this.cachedElements = {
      target: this.element.querySelector(CONTAINER_SELECTOR),
      baseInput: this.element.querySelector(DROPDOWN_INPUT),
      trigger: this.element.querySelector(TRIGGER_SELECTOR),
      customTrigger: this.element.querySelector(CUSTOM_DISPLAY_SELECTOR),
      dropdownWrapper: this.element.querySelector(".dropdown_wrapper"),
      placeholder: this.element.querySelector(DROPDOWN_PLACEHOLDER),
    };
  }

  /** Options live under `[data-dropdown-container]`; use this when the menu is portaled to body. */
  queryAllOptions() {
    const root = this.target || this.element;
    return root.querySelectorAll(OPTION_SELECTOR);
  }

  get isAsync() {
    return this.element.dataset.pbDropdownAsync === "true";
  }

  get allowOptionCreation() {
    return this.element.dataset.pbDropdownAllowOptionCreation === "true";
  }

  removeCreationOption() {
    const row = this.target?.querySelector("[data-dropdown-create-option]");
    if (row) {
      this.resetFocus();
      row.remove();
    }
  }

  updateCreationOption(term) {
    if (!this.allowOptionCreation) return;
    this.removeCreationOption();

    const searchIsPending = this.asyncSearchTimer != null || this.element.getAttribute("aria-busy") === "true";
    if (this.isDisabled || searchIsPending || this.asyncCreationFailed) return;

    const options = Array.from(this.queryAllOptions()).map((node) => (
      JSON.parse(node.dataset.dropdownOptionLabel)
    ));
    let selected = [];
    if (this.isMultiSelect) {
      selected = Array.from(this.selectedOptions).map(JSON.parse);
    } else if (this.selectedOptionJson) {
      selected = [JSON.parse(this.selectedOptionJson)];
    }

    const option = getCreationOption(term, [...options, ...selected]);
    if (!option) return;

    const row = this.buildOptionElement(option);
    row.removeAttribute("id");
    row.dataset.dropdownCreateOption = "true";
    row.querySelector(".pb_body_kit_light").textContent = `Create "${option.label}"`;
    this.getOptionsParent()?.appendChild(row);
    this.removeNoOptionsMessage();
    this.showElement(this.target);
    this.updateArrowDisplay(true);
    this.resetFocus();
    row.classList.add("pb_dropdown_option_focused");
    this.keyboardHandler.focusedOptionIndex = Array.from(this.queryAllOptions()).indexOf(row);
    this.adjustDropdownHeight();
  }

  get canOpenMenu() {
    return !this.isAsync || Boolean(this.searchBar || this.hasAsyncMenuContent());
  }

  hasAsyncMenuContent() {
    return Boolean(this.target?.querySelector(
      `${OPTION_SELECTOR}, [data-dropdown-async-status], .dropdown_no_options`,
    ));
  }

  // An empty async menu opens with the empty message until a search replaces it.
  showAsyncEmptyState() {
    if (!this.isAsync || this.hasAsyncMenuContent()) return;
    this.getOptionsParent()?.replaceChildren();
    this.showNoOptionsMessage("No results found");
  }

  // Selected multi-select rows stay in the list with display:none. An open menu of only
  // those rows collapses to its border, so show the empty message instead.
  showMultiSelectEmptyMenu() {
    if (!this.isMultiSelect) return;
    const options = Array.from(this.queryAllOptions());
    if (!options.length) return;
    const hasVisibleOption = options.some((opt) => opt.style.display !== "none");
    if (hasVisibleOption) this.removeNoOptionsMessage();
    else this.showNoOptionsMessage(this.isAsync ? "No results found" : undefined);
  }

  asyncRequestId = 0;
  asyncResultsCleared = false;
  asyncSearchInterrupted = false;
  asyncSettledOptions = [];
  asyncHasSettledSearch = false;
  asyncSettledEmpty = null;
  asyncSettledStatus = null;
  selectedOptionJson = null;
  selectedOptions = new Set();
  clearBtn = null;

  connect() {
    // Store instance on element for DatePicker sync
    this.element._pbDropdownInstance = this;
    this.cacheElements();

    this.portalHost = null;
    this.useMenuPortal = false;
    this.floatingOwnerId = null;
    this._floatingResolved = false;
    this.portalShell = null;
    this._portalParent = null;
    this._portalNext = null;
    this.boundApplyPortalPosition = () => {
      if (this.target?.classList.contains("open")) {
        this.applyPortalPosition();
      }
    };

    this.activeStyleClass = this.activeStyleClasses();
    if (this.activeStyleClass) {
      this.queryAllOptions().forEach((opt) => {
        opt.classList.add(...this.activeStyleClass.split(" "));
      });
    }
    this.keyboardHandler = new PbDropdownKeyboard(this);
    this.isDisabled = this.element.dataset.pbDropdownDisabled === "true";
    this.isMultiSelect = this.element.dataset.pbDropdownMultiSelect === "true";
    this.enablePillReorder = this.isMultiSelect
      && this.element.dataset.pbDropdownEnablePillReorder === "true";
    this.externalDragHandle = this.enablePillReorder
      && this.element.dataset.pbDropdownExternalDragHandle === "true";
    this.showPillIndex = this.isMultiSelect
      && this.element.dataset.pbDropdownShowPillIndex === "true";
    this.closeOnClick = this.element.dataset.pbDropdownCloseOnClick || "any";
    this.formPillProps = this.element.dataset.formPillProps
      ? JSON.parse(this.element.dataset.formPillProps)
      : {};
    this.pillReorderCleanup = null;
    const baseInput = this.baseInput;
    // Preserve original attributes when Turbo reconnects to the mutated DOM.
    if (this.isMultiSelect && baseInput) {
      if (baseInput.dataset.pbDropdownInputName == null) {
        baseInput.dataset.pbDropdownInputName = baseInput.getAttribute("name") || "";
        baseInput.dataset.pbDropdownOriginallyRequired = String(baseInput.required);
      }
      this.baseInputName = baseInput.dataset.pbDropdownInputName;
      this.wasOriginallyRequired = baseInput.dataset.pbDropdownOriginallyRequired === "true";
    } else {
      this.wasOriginallyRequired = Boolean(baseInput && baseInput.required);
    }
    // Apply context options before default value so SSR/default selection matches current context
    this.bindContextSelector();
    this.setDefaultValue();
    this.bindEventListeners();
    this.bindSearchInput();
    this.updateArrowDisplay(false);
    this.handleFormValidation();
    this.handleFormReset();
    this.bindSearchBar();
    this.updatePills();

    this.clearBtn = this.element.querySelector(CLEAR_ICON_SELECTOR);
    this.isClearable = this.element.dataset.pbDropdownClearable !== "false";
    if (this.clearBtn) {
      this.clearBtn.style.display = "none";
      this.clearBtnHandler = (e) => {
        e.stopPropagation();
        this.clearSelection();
      }
      this.clearBtn.addEventListener("click", this.clearBtnHandler);
    }
    this.updateClearButton();
    this.applyDisabledState();

    // Listen for clear, select, and updateOptions events from external source
    this.handleClearEventBound = this.handleClearEvent.bind(this);
    document.addEventListener("pb:dropdown:clear", this.handleClearEventBound);
    this.handleSelectEventBound = this.handleSelectEvent.bind(this);
    document.addEventListener("pb:dropdown:select", this.handleSelectEventBound);
    this.handleUpdateOptionsEventBound = this.handleUpdateOptionsEvent.bind(this);
    document.addEventListener("pb:dropdown:updateOptions", this.handleUpdateOptionsEventBound);

    // Listen for custom_event_type to clear on custom events
    const customEventTypeString = this.element.dataset.customEventType;
    if (customEventTypeString) {
      this.customClearEventTypes = customEventTypeString
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);
      this.handleCustomClearBound = this.handleCustomClearEvent.bind(this);
      this.customClearEventTypes.forEach((eventType) => {
        document.addEventListener(eventType, this.handleCustomClearBound);
      });
    }

    // Listen for options_event_type to replace options on custom/Turbo events
    const optionsEventTypeString = this.element.dataset.optionsEventType;
    if (optionsEventTypeString) {
      this.optionsEventTypes = optionsEventTypeString
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);
      this.handleOptionsEventTypeBound = this.handleOptionsEventType.bind(this);
      this.optionsEventTypes.forEach((eventType) => {
        document.addEventListener(eventType, this.handleOptionsEventTypeBound);
      });
    }
  }

  disconnect() {
    if (typeof this.pillReorderCleanup === "function") {
      this.pillReorderCleanup();
      this.pillReorderCleanup = null;
    }
    if (this.isAsync) this.cancelAsyncSearch();
    if (this.formElement && this.formResetHandler) {
      this.formElement.removeEventListener("reset", this.formResetHandler);
      this.formResetHandler = null;
    }
    this.unmountPortalMenu();

    // Clean up stored instance reference
    if (this.element._pbDropdownInstance === this) {
      delete this.element._pbDropdownInstance
    }

    // Clean up keyboard handler
    if (this.keyboardHandler && typeof this.keyboardHandler.disconnect === 'function') {
      this.keyboardHandler.disconnect()
    }

    // Clean up custom trigger click listener
    if (this.customTriggerClickHandler) {
      const customTrigger = this.customTrigger || this.element
      customTrigger.removeEventListener('click', this.customTriggerClickHandler)
    }

    // Clean up target click listener
    if (this.handleOptionClickBound) {
      this.target.removeEventListener('click', this.handleOptionClickBound)
    }

    // Clean up document click listener
    if (this.handleDocumentClickBound) {
      document.removeEventListener('click', this.handleDocumentClickBound, true)
    }

    // Clean up search bar listener
    if (this.searchBar && this.searchBarHandler) {
      this.searchBar.removeEventListener('input', this.searchBarHandler)
    }

    // Clean up search input listeners
    if (this.searchInput) {
      if (this.searchInputFocusHandler) {
        const trigger = this.trigger
        if (trigger) {
          trigger.removeEventListener('click', this.searchInputFocusHandler)
        }
      }
      if (this.searchInputHandler) {
        this.searchInput.removeEventListener('input', this.searchInputHandler)
      }
    }

    // Clean up clear button listener
    if (this.clearBtn && this.clearBtnHandler) {
      this.clearBtn.removeEventListener('click', this.clearBtnHandler)
    }

    // Clean up external clear/select listeners
    if (this.handleClearEventBound) {
      document.removeEventListener("pb:dropdown:clear", this.handleClearEventBound)
    }
    if (this.handleSelectEventBound) {
      document.removeEventListener("pb:dropdown:select", this.handleSelectEventBound)
    }
    if (this.handleUpdateOptionsEventBound) {
      document.removeEventListener("pb:dropdown:updateOptions", this.handleUpdateOptionsEventBound)
    }
    if (this.customClearEventTypes && this.handleCustomClearBound) {
      this.customClearEventTypes.forEach((eventType) => {
        document.removeEventListener(eventType, this.handleCustomClearBound)
      })
    }
    if (this.optionsEventTypes && this.handleOptionsEventTypeBound) {
      this.optionsEventTypes.forEach((eventType) => {
        document.removeEventListener(eventType, this.handleOptionsEventTypeBound)
      })
    }
    if (this.contextElement && this.handleContextChangeBound) {
      this.contextElement.removeEventListener("change", this.handleContextChangeBound)
    }
  }

  updateClearButton() {
    if (!this.clearBtn) return;
    if (!this.isClearable) {
      this.clearBtn.style.display = "none";
      return;
    }
    const hasSelection = this.isMultiSelect
      ? this.selectedOptions.size > 0
      : Boolean(this.baseInput?.value);

    const hasQuery = this.isAsync && Boolean(this.searchInput?.value || this.searchBar?.value);
    this.clearBtn.style.display = hasSelection || hasQuery ? "" : "none";
  }

  applyDisabledState() {
    if (!this.isDisabled) return;

    const focusableSelector = [
      "a[href]",
      "button",
      "input",
      "select",
      "textarea",
      "[tabindex]",
      "[contenteditable='true']",
      CLEAR_ICON_SELECTOR,
      DOWN_ARROW_SELECTOR,
      UP_ARROW_SELECTOR,
    ].join(",");

    this.element.querySelectorAll(focusableSelector).forEach((element) => {
      element.setAttribute("aria-disabled", "true");
      element.setAttribute("tabindex", "-1");

      if (element.matches("input, select, textarea, button")) {
        element.setAttribute("disabled", "disabled");
      }
    });

    this.element.querySelectorAll(`${CLEAR_ICON_SELECTOR}, ${DOWN_ARROW_SELECTOR}, ${UP_ARROW_SELECTOR}`).forEach((icon) => {
      icon.setAttribute("aria-hidden", "true");
      icon.setAttribute("focusable", "false");
    });

    this.hideElement(this.target);
    this.updateArrowDisplay(false);
  }

  bindEventListeners() {
    const customTrigger = this.customTrigger || this.element;
    this.customTriggerClickHandler = (e) => {
      const label = e.target.closest(LABEL_SELECTOR);
      if (this.isDisabled) return;
      if (label && label.htmlFor) {
        const trigger = this.element.querySelector(
          `#${CSS.escape(label.htmlFor)}`,
        );
        if (trigger) {
          trigger.focus();
        }
      }
      if (
        this.closeOnClick === "outside" &&
        this.target?.contains(e.target)
      ) {
        return;
      }
      this.toggleElement(this.target);
    }
    customTrigger.addEventListener("click", this.customTriggerClickHandler);

    this.handleOptionClickBound = this.handleOptionClick.bind(this)
    this.target.addEventListener("click", this.handleOptionClickBound);
    
    this.handleDocumentClickBound = this.handleDocumentClick.bind(this)
    document.addEventListener(
      "click",
      this.handleDocumentClickBound,
      true,
    );
  }

  bindSearchBar() {
    this.searchBar = this.element.querySelector(SEARCH_BAR_SELECTOR);
    if (!this.searchBar) return;

    this.searchBarHandler = (e) => this.handleSearchInput(e.target.value, e.target)
    this.searchBar.addEventListener('input', this.searchBarHandler);
  }

  bindSearchInput() {
    this.searchInput = this.element.querySelector(SEARCH_INPUT_SELECTOR);
    if (!this.searchInput) return;

    // Focus the input when anyone clicks the wrapper
    this.searchInputFocusHandler = () => this.searchInput.focus()
    this.trigger?.addEventListener('click', this.searchInputFocusHandler);

    // Live filter
    this.searchInputHandler = (e) => this.handleSearchInput(e.target.value, e.target)
    this.searchInput.addEventListener('input', this.searchInputHandler);
  }

  adjustDropdownHeight() {
    if (this.target.classList.contains("open")) {
      const el = this.target;
      const shouldConstrain = el.classList.contains("constrain_height");
      el.style.height = "auto";
      requestAnimationFrame(() => {
        // Closing sets display:none before this frame. Measuring then stores a 0 height.
        if (!el.classList.contains("open")) return;
        if (shouldConstrain) {
          // Calculate 18em in pixels (matches SCSS max-height: 18em)
          const fontSize = parseFloat(getComputedStyle(el).fontSize) || 16;
          const maxHeight = fontSize * 18;
          const scrollHeight = el.scrollHeight;
          const newHeight = Math.min(scrollHeight, maxHeight);
          el.offsetHeight; // force reflow
          el.style.height = newHeight + "px";
        } else {
          el.offsetHeight; // force reflow
          el.style.height = el.scrollHeight + "px";
        }
        if (this.useMenuPortal) {
          this.applyPortalPosition();
        }
      });
    }
  }

  adjustDropdownPosition(container) {
    if (!container) return;

    if (this.useMenuPortal) {
      this.applyPortalPosition();
      return;
    }

    const wrapper = this.dropdownWrapper;
    if (!wrapper) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const h = container.getBoundingClientRect().height || container.scrollHeight;
    const spaceBelow = window.innerHeight - wrapperRect.bottom;
    const spaceAbove = wrapperRect.top;

    // If not enough space below but enough space above, position above
    if (spaceBelow < h + 10 && spaceAbove >= h + 10) {
      container.style.top = "auto";
      container.style.bottom = "calc(100% + 5px)";
      container.style.marginTop = "0";
      container.style.marginBottom = "0";
    } else {
      // Default: position below
      container.style.top = "";
      container.style.bottom = "";
      container.style.marginTop = "";
      container.style.marginBottom = "";
    }
  }

  ensureFloatingPortalConfig() {
    if (this._floatingResolved) return;
    this.portalHost = resolvePortaledKitHost(this.element, null);
    this.floatingOwnerId = resolveFloatingOwnerId(this.element);
    this.useMenuPortal = Boolean(this.portalHost);
    this._floatingResolved = true;
  }

  mountPortalMenu(container) {
    if (!this.useMenuPortal || !this.portalHost) return;
    if (container.dataset.pbDropdownPortaled === "true") return;

    this._portalParent = container.parentNode;
    this._portalNext = container.nextSibling;

    const shell = document.createElement("div");
    shell.className = `${this.element.className} pb_dropdown_floating_shell`.trim();
    if (this.floatingOwnerId) {
      shell.setAttribute(PB_FLOATING_OWNER_ATTR, this.floatingOwnerId);
    }

    const innerWrap = document.createElement("div");
    const hasError = this.dropdownWrapper?.classList.contains("error");
    innerWrap.className = hasError ? "dropdown_wrapper error" : "dropdown_wrapper";
    Object.assign(innerWrap.style, {
      background: "transparent",
      border: "none",
      boxShadow: "none",
      margin: "0",
      minHeight: "0",
      padding: "0",
      position: "static",
    });

    innerWrap.appendChild(container);
    shell.appendChild(innerWrap);
    this.portalHost.appendChild(shell);
    this.portalShell = shell;
    container.dataset.pbDropdownPortaled = "true";
  }

  unmountPortalMenu() {
    if (this._unsubscribePortalReposition) {
      this._unsubscribePortalReposition();
      this._unsubscribePortalReposition = null;
    }
    if (this.boundApplyPortalPosition) {
      window.removeEventListener("resize", this.boundApplyPortalPosition);
    }
    if (!this.target || this.target.dataset.pbDropdownPortaled !== "true") {
      return;
    }
    delete this.target.dataset.pbDropdownPortaled;
    clearDropdownPortalPanelStyles(this.target);

    if (this._portalParent && this.target) {
      if (this._portalNext && this._portalNext.parentNode === this._portalParent) {
        this._portalParent.insertBefore(this.target, this._portalNext);
      } else {
        this._portalParent.appendChild(this.target);
      }
    }
    this._portalParent = null;
    this._portalNext = null;

    if (this.portalShell) {
      this.portalShell.remove();
    }
    this.portalShell = null;
  }

  applyPortalPosition() {
    if (!this.useMenuPortal || !this.portalHost || !this.target || !this.dropdownWrapper) {
      return;
    }
    positionDropdownPortalToWrapper({
      panel: this.target,
      wrapperViewportRect: this.dropdownWrapper.getBoundingClientRect(),
      positionHost: this.portalHost,
    });
  }

  emitInputChange(value, reason) {
    this.element.dispatchEvent(new CustomEvent("pb:dropdown:input", {
      bubbles: true,
      detail: { value, reason },
    }));
  }

  handleSearchInput(term, source) {
    if (this.isDisabled) return;
    this.handleSearch(term);
    if (!this.isAsync) return;
    if (!this.isMultiSelect && source === this.searchInput && term === "" && this.baseInput.value) {
      this.selectedOptionJson = null;
      this.baseInput.value = "";
      this.emitSelectionChange();
    }
    this.updateClearButton();
    this.emitInputChange(term, "input");
  }

  handleSearch(term = "") {
    if (this.isDisabled) return;
    if (this.allowOptionCreation) this.removeCreationOption();
    if (this.isAsync) {
      this.searchAsync(term);
      return;
    }
    const lcTerm = term.toLowerCase();
    let hasMatch = false;
    this.queryAllOptions().forEach((opt) => {
      //make it so that if the option is selected, it will not show up in the search results
      if (
        this.isMultiSelect &&
        this.selectedOptions.has(opt.dataset.dropdownOptionLabel)
      ) {
        opt.style.display = "none";
        return;
      }
      const option = JSON.parse(opt.dataset.dropdownOptionLabel);
      const label = option.label.toString().toLowerCase();

      // hide or show option
      const match = label.includes(lcTerm) ||
        (this.allowOptionCreation && matchesOptionExactly(option, term));
      opt.style.display = match ? "" : "none";
      if (match) hasMatch = true;
    });

    this.removeNoOptionsMessage();
    if (!hasMatch) {
      this.showNoOptionsMessage();
    }
    this.updateCreationOption(term);
    this.adjustDropdownHeight();
  }

  cancelAsyncSearch() {
    this.asyncRequestId += 1;
    clearTimeout(this.asyncSearchTimer);
    this.asyncSearchTimer = null;
    clearTimeout(this.asyncLoadingTimer);
    this.element.setAttribute("aria-busy", "false");
    this.target?.querySelector("[data-dropdown-async-status]")?.remove();
  }

  // "Emily S" can miss after "Emily Smith" was already loaded. Keep labels that contain the query.
  settledOptionsMatchingQuery(term) {
    const query = String(term).trim().toLowerCase().replace(/\s+/g, " ");
    if (!query || !this.asyncHasSettledSearch) return [];
    return this.asyncSettledOptions.filter((node) => {
      try {
        const label = String(JSON.parse(node.dataset.dropdownOptionLabel).label);
        return label.trim().toLowerCase().replace(/\s+/g, " ").includes(query);
      } catch {
        return false;
      }
    });
  }

  // The selected-label filter hides siblings before the next search snapshots them.
  // A shorter query can match those rows, so the clone must not keep display:none.
  cloneSettledAsyncOption(node) {
    const option = node.cloneNode(true);
    const selected = this.isMultiSelect && Array.from(this.selectedOptions).some((raw) =>
      this.sameOption(raw, option.dataset.dropdownOptionLabel),
    );
    option.style.display = selected ? "none" : "";
    return option;
  }

  rememberSettledAsyncResults() {
    const parent = this.getOptionsParent();
    const optionNodes = parent ? Array.from(parent.children) : [];
    this.asyncSettledOptions = optionNodes
      .filter((node) => !this.allowOptionCreation || !node.hasAttribute("data-dropdown-create-option"))
      .map((node) => node.cloneNode(true));
    const empty = this.target?.querySelector(".dropdown_no_options");
    const status = this.target?.querySelector("[data-dropdown-async-status]");
    this.asyncSettledEmpty = empty ? empty.cloneNode(true) : null;
    this.asyncSettledStatus = status ? status.cloneNode(true) : null;
  }

  restoreSettledAsyncResults() {
    const parent = this.getOptionsParent();
    if (!parent) return;
    parent.replaceChildren(...this.asyncSettledOptions.map((node) => node.cloneNode(true)));
    this.removeNoOptionsMessage();
    this.target?.querySelector("[data-dropdown-async-status]")?.remove();
    if (this.asyncSettledEmpty && this.target) this.target.appendChild(this.asyncSettledEmpty.cloneNode(true));
    if (this.asyncSettledStatus && this.target) this.target.appendChild(this.asyncSettledStatus.cloneNode(true));
    this.asyncResultsCleared = false;
  }

  forgetSettledAsyncResults() {
    this.asyncResultsCleared = false;
    this.asyncSearchInterrupted = false;
    this.asyncSettledOptions = [];
    this.asyncHasSettledSearch = false;
    this.asyncSettledEmpty = null;
    this.asyncSettledStatus = null;
  }

  // A multi-select pick ends the search. The next open shows the empty message until a new query.
  dropAsyncSearchResults() {
    this.forgetSettledAsyncResults();
    this.cancelAsyncSearch();
    this.getOptionsParent()?.replaceChildren();
    this.removeNoOptionsMessage();
    this.target?.querySelector("[data-dropdown-async-status]")?.remove();
    this.resetFocus();
  }

  clearAsyncResults() {
    this.forgetSettledAsyncResults();
    this.cancelAsyncSearch();
    this.getOptionsParent()?.replaceChildren();
    this.removeNoOptionsMessage();
    this.resetFocus();
    this.unmountPortalMenu();
    this.target.classList.remove("open");
    this.target.classList.add("close");
    this.target.style.height = "0px";
    this.updateArrowDisplay(false);
  }

  showAsyncStatus(message) {
    this.target.querySelector("[data-dropdown-async-status]")?.remove();
    const status = document.createElement("div");
    status.dataset.dropdownAsyncStatus = "true";
    status.className = "pb_body_kit_light pb_item_kit p_xs display_flex justify_content_center";
    status.setAttribute("role", "status");
    status.textContent = message;
    this.target.appendChild(status);
    this.adjustDropdownHeight();
    this.applyPortalPosition();
  }

  searchAsync(term) {
    this.cancelAsyncSearch();
    this.asyncSearchInterrupted = false;
    this.asyncCreationFailed = false;

    const minimumLength = Number(this.element.dataset.pbDropdownSearchTermMinimumLength ?? 3);
    if (term.length < minimumLength) {
      if (!this.searchBar) {
        this.clearAsyncResults();
      } else {
        this.forgetSettledAsyncResults();
        this.getOptionsParent()?.replaceChildren();
        this.removeNoOptionsMessage();
        this.target?.querySelector("[data-dropdown-async-status]")?.remove();
        this.resetFocus();
        if (this.target?.classList.contains("open")) this.showAsyncEmptyState();
        this.adjustDropdownHeight();
        this.applyPortalPosition();
      }
      this.updateCreationOption(term);
      return;
    }

    const requestId = this.asyncRequestId;
    const delay = Number(this.element.dataset.pbDropdownSearchDebounceTimeout ?? 250);
    this.asyncSearchTimer = setTimeout(() => {
      this.asyncSearchTimer = null;
      // Snapshot the list that stayed visible during debounce so a cancelled request can restore it.
      if (!this.asyncResultsCleared) this.rememberSettledAsyncResults();
      this.asyncResultsCleared = true;
      this.getOptionsParent()?.replaceChildren();
      this.removeNoOptionsMessage();
      this.target?.querySelector("[data-dropdown-async-status]")?.remove();
      this.resetFocus();
      this.element.setAttribute("aria-busy", "true");
      this.showAsyncStatus("Loading…");
      this.showElement(this.target);
      this.updateArrowDisplay(true);

      let settled = false;
      const finish = (options, failed = false) => {
        if (settled || requestId !== this.asyncRequestId || this.isDisabled) return;
        if (!failed && !Array.isArray(options)) return;
        settled = true;
        clearTimeout(this.asyncLoadingTimer);
        this.element.setAttribute("aria-busy", "false");
        this.target.querySelector("[data-dropdown-async-status]")?.remove();
        const parent = this.getOptionsParent();
        parent.replaceChildren();
        const visibleOptions = !failed && !options.length ? this.settledOptionsMatchingQuery(term) : options;
        visibleOptions.forEach((result) => parent.appendChild(
          result instanceof Node ? this.cloneSettledAsyncOption(result) : this.buildAsyncOptionElement(result),
        ));
        // The default option lives outside the menu, so mark the mounted row that shares its id.
        this.markSelectedAsyncResult();
        if (failed) this.showAsyncStatus("Unable to load options");
        else {
          this.asyncHasSettledSearch = true;
          if (!visibleOptions.length) this.showNoOptionsMessage("No results found");
        }
        this.asyncResultsCleared = false;
        this.rememberSettledAsyncResults();
        this.resetOptionFilterForSelectedLabel();
        this.asyncCreationFailed = failed;
        this.updateCreationOption(term);
        this.adjustDropdownHeight();
        this.applyPortalPosition();
      };

      // A missing handler or unfinished request must not leave the kit loading forever.
      this.asyncLoadingTimer = setTimeout(() => finish([], true), 15000);
      this.element.dispatchEvent(new CustomEvent("pb:dropdown:search", {
        bubbles: true,
        detail: {
          searchingFor: term,
          setResults: (options) => finish(options),
          setError: () => finish([], true),
        },
      }));
    }, delay);
  }

  // A default_value selection is not in the menu until a search renders that id.
  markSelectedAsyncResult() {
    if (!this.isAsync || this.isMultiSelect) return;
    const selectedId = this.baseInput?.value || this.selectedOptionId();
    if (!selectedId) return;

    let matched = null;
    this.queryAllOptions().forEach((opt) => {
      opt.classList.remove("pb_dropdown_option_selected");
      try {
        const payload = JSON.parse(opt.dataset.dropdownOptionLabel);
        if (String(payload.id) === String(selectedId)) matched = opt;
      } catch {
        // ignore invalid option payloads
      }
    });
    if (matched) matched.classList.add("pb_dropdown_option_selected");
  }

  selectedOptionId() {
    if (!this.selectedOptionJson) return "";
    try {
      const id = JSON.parse(this.selectedOptionJson).id;
      return id == null ? "" : String(id);
    } catch {
      return "";
    }
  }

  // Synchronous autocomplete shows every option when the input is the selected label.
  // Async autocomplete narrows to labels that contain that text.
  resetOptionFilterForSelectedLabel() {
    if (this.isMultiSelect || !this.searchInput) return;

    const selectedOption = Array.from(this.queryAllOptions()).find((opt) =>
      opt.classList.contains("pb_dropdown_option_selected"),
    );
    if (!selectedOption) return;

    try {
      const selectedLabel = JSON.parse(
        selectedOption.dataset.dropdownOptionLabel,
      ).label;
      if (this.searchInput.value !== selectedLabel) return;

      if (!this.isAsync) {
        this.queryAllOptions().forEach((opt) => {
          opt.style.display = "";
        });
        this.removeNoOptionsMessage();
        return;
      }

      const query = String(selectedLabel).toLowerCase();
      let hasMatch = false;
      this.queryAllOptions().forEach((opt) => {
        let label = "";
        try {
          label = String(JSON.parse(opt.dataset.dropdownOptionLabel).label);
        } catch {
          opt.style.display = "none";
          return;
        }
        const match = label.toLowerCase().includes(query);
        opt.style.display = match ? "" : "none";
        if (match) hasMatch = true;
      });
      if (hasMatch) this.removeNoOptionsMessage();
      else this.showNoOptionsMessage("No results found");
      this.adjustDropdownHeight();
    } catch {
      // ignore invalid option payloads
    }
  }

  showNoOptionsMessage(fallbackText = "no option") {
    if (this.target?.querySelector(".dropdown_no_options")) return;

    const noOptionElement = document.createElement("div");
    noOptionElement.className =
      "pb_body_kit_light dropdown_no_options pb_item_kit p_xs display_flex justify_content_center";
    noOptionElement.textContent = this.element.dataset.pbDropdownNoOptionsText || fallbackText;

    this.target.appendChild(noOptionElement);
  }

  removeNoOptionsMessage() {
    const existing = this.target?.querySelector(".dropdown_no_options");
    if (existing) {
      existing.remove();
    }
  }

  handleOptionClick(event) {
    if (this.isDisabled) return;
    const option = event.target.closest(OPTION_SELECTOR);
    const hiddenInput = this.baseInput;

    if (option) {
      if (option.dataset.dropdownOptionDisabled === "true") return;

      const value = option.dataset.dropdownOptionLabel;
      if (this.isMultiSelect) {
        const selected = Array.from(this.selectedOptions).find((raw) =>
          this.sameOption(raw, value),
        );
        if (selected) {
          this.selectedOptions.delete(selected);
        } else {
          this.selectedOptions.add(value);
        }
        this.updatePills();
        this.syncHiddenInputs();
        if (this.isMultiSelect && this.searchInput) this.searchInput.value = "";
        if (this.isAsync && this.isMultiSelect) {
          if (this.searchBar) this.searchBar.value = "";
          this.dropAsyncSearchResults();
          if (this.target.classList.contains("open")) {
            this.showAsyncEmptyState();
            this.adjustDropdownHeight();
          }
        } else if (this.searchInput && this.isMultiSelect) {
          this.handleBackspaceClear();
        }
      } else {
        hiddenInput.value = JSON.parse(value).id;
      }

      this.clearFormValidation(hiddenInput);
      this.onOptionSelected(value, option);
      if (this.allowOptionCreation) this.removeCreationOption();
      this.updateClearButton();
    }
  }

  handleDocumentClick(event) {
    if (event.target.closest(SEARCH_BAR_SELECTOR) && (!this.isAsync || this.target.contains(event.target))) return;
    const shouldCloseOnOutsideClick =
      this.closeOnClick === "outside" || this.closeOnClick === "any";
    if (
      shouldCloseOnOutsideClick &&
      this.isClickOutside(event) &&
      (this.target.classList.contains("open") || (this.isAsync && this.asyncSearchTimer != null))
    ) {
      this.hideElement(this.target);
      this.updateArrowDisplay(false);
    }
  }

  // ----- External events handling section -----

  get optionsByContext() {
    return this.element.dataset.pbDropdownOptionsByContext
      ? JSON.parse(this.element.dataset.pbDropdownOptionsByContext)
      : null;
  }

  get contextElement() {
    const selector = this.element.dataset.pbDropdownContextSelector;
    if (!selector) return null;

    return (
      this.element.parentNode?.querySelector(`#${CSS.escape(selector)}`) ||
      this.element.closest(`#${CSS.escape(selector)}`) ||
      document.getElementById(selector)
    );
  }

  get clearOnContextChange() {
    return this.element.dataset.pbDropdownClearOnContextChange !== "false";
  }

  bindContextSelector() {
    const contextEl = this.contextElement;
    if (!contextEl || !this.optionsByContext) return;

    this.handleContextChangeBound = this.handleContextChange.bind(this);
    contextEl.addEventListener("change", this.handleContextChangeBound);

    // Sync to the current context value on connect (default/restored select), without clearing
    this.applyOptionsForCurrentContext({ clearSelection: false });
  }

  applyOptionsForCurrentContext({ clearSelection } = {}) {
    if (this.isDisabled || !this.optionsByContext) return;

    const options = this.optionsByContext[this.contextElement?.value] || [];
    this.replaceOptions(options, {
      clearSelection:
        clearSelection != null ? clearSelection : this.clearOnContextChange,
    });
  }

  handleContextChange() {
    this.applyOptionsForCurrentContext();
  }

  normalizeOption(option) {
    const normalized = { ...option };
    if (normalized.id == null && normalized.value != null) {
      normalized.id = normalized.value;
    }
    if (normalized.value == null && normalized.id != null) {
      normalized.value = normalized.id;
    }
    return normalized;
  }

  activeStyleClasses() {
    const raw = this.element.dataset.pbDropdownActiveStyle;
    if (!raw) return "";
    try {
      const style = JSON.parse(raw);
      const background = style.background_color || style.backgroundColor;
      const font = style.font_color || style.fontColor;
      return [background && `bg-${background}`, font && `font-${font}`].filter(Boolean).join(" ");
    } catch {
      return "";
    }
  }

  // Synchronous options keep their rendered hash. Async results copy value onto id.
  sameOption(left, right) {
    const leftPayload = typeof left === "string" ? left : JSON.stringify(left);
    const rightPayload = typeof right === "string" ? right : JSON.stringify(right);
    const leftOption = typeof left === "string" ? JSON.parse(left) : left;
    const rightOption = typeof right === "string" ? JSON.parse(right) : right;
    const leftKey = leftOption?.id ?? leftOption?.value;
    const rightKey = rightOption?.id ?? rightOption?.value;
    if (leftKey == null && rightKey == null) return leftPayload === rightPayload;
    if (leftKey == null || rightKey == null) return false;
    return String(leftKey) === String(rightKey);
  }

  getOptionsParent() {
    const container = this.target;
    if (!container) return null;

    return container.querySelector(".pb_list_kit") || container;
  }

  buildAsyncOptionElement(result) {
    const richContent = result.option && (result.content instanceof Element || result.content instanceof DocumentFragment);
    const option = this.buildOptionElement(richContent ? result.option : result, richContent ? result.content : null);
    if (this.isMultiSelect && Array.from(this.selectedOptions).some((raw) => this.sameOption(raw, option.dataset.dropdownOptionLabel))) {
      option.style.display = "none";
    }
    return option;
  }

  buildOptionElement(option, content) {
    const normalized = this.normalizeOption(option);
    const disabled = normalized.disabled === true;
    const optionEl = document.createElement("div");
    optionEl.className = `pb_dropdown_option_list${disabled ? " disabled" : ""}${this.activeStyleClass ? ` ${this.activeStyleClass}` : ""}`;
    if (normalized.id != null && normalized.id !== "") {
      optionEl.id = String(normalized.id);
    }
    optionEl.setAttribute("aria-disabled", disabled ? "true" : "false");
    optionEl.dataset.dropdownOptionLabel = JSON.stringify(normalized);
    optionEl.dataset.dropdownOptionDisabled = disabled ? "true" : "false";

    const listItem = document.createElement("div");
    listItem.className =
      "pb_list_item_kit display_flex justify_content_center p_none cursor_pointer";

    const wrapper = document.createElement("div");
    wrapper.className = disabled
      ? "dropdown_option_wrapper disabled"
      : "dropdown_option_wrapper";

    if (content) {
      wrapper.appendChild(content.cloneNode(true));
    } else {
      const body = document.createElement("div");
      body.className = "pb_body_kit_light";
      body.textContent =
        normalized.label != null ? String(normalized.label) : "";
      wrapper.appendChild(body);
    }
    listItem.appendChild(wrapper);
    optionEl.appendChild(listItem);

    return optionEl;
  }

  replaceOptions(options, { clearSelection = true } = {}) {
    if (this.isDisabled || !Array.isArray(options)) return;

    const parent = this.getOptionsParent();
    if (!parent) return;

    if (this.isAsync) this.cancelAsyncSearch();

    // Clear option nodes and any SSR empty-state ("No option") placeholder
    parent.replaceChildren();
    this.removeNoOptionsMessage();

    options.forEach((option) => {
      parent.appendChild(this.buildOptionElement(option));
    });

    // Clear typed filters and keyboard focus so new options aren't left unfiltered /
    // focused against stale indexes from the previous list
    this.resetInteractiveOptionState();

    if (clearSelection) {
      this.clearSelection("clear", { preserveOptions: true });
    } else {
      this.reconcileSelectionWithOptions();
    }

    if (this.isAsync) {
      this.asyncSearchInterrupted = false;
      this.asyncResultsCleared = false;
      this.rememberSettledAsyncResults();
    }

    if (this.target?.classList.contains("open")) {
      this.adjustDropdownHeight();
    }
  }

  resetInteractiveOptionState() {
    if (this.allowOptionCreation) this.removeCreationOption();
    this.resetFocus();

    if (this.searchBar) {
      this.searchBar.value = "";
    }

    if (this.searchInput) {
      this.searchInput.value = "";
    }
  }

  reconcileSelectionWithOptions() {
    const optionEls = Array.from(this.queryAllOptions());
    const optionsById = new Map();
    optionEls.forEach((opt) => {
      try {
        const optionData = JSON.parse(opt.dataset.dropdownOptionLabel);
        if (optionData?.id != null) {
          optionsById.set(String(optionData.id), opt);
        }
      } catch {
        // ignore invalid option payloads
      }
    });

    if (this.isMultiSelect) {
      const keptIds = Array.from(this.selectedOptions)
        .map((raw) => {
          try {
            return JSON.parse(raw).id;
          } catch {
            return null;
          }
        })
        .filter((id) => id != null && optionsById.has(String(id)));

      if (keptIds.length === 0) {
        this.clearSelection("clear", { preserveOptions: true });
        return;
      }

      // Rebuild from current option payloads so labels/values stay in sync
      this.setSelectionByOptionIds(keptIds);
      return;
    }

    const currentId = this.baseInput?.value;
    if (!currentId || !optionsById.has(currentId)) {
      this.clearSelection("clear", { preserveOptions: true });
      return;
    }

    // Re-apply selection so trigger/autocomplete use the updated option payload
    this.setSelectionByOptionId(currentId);
  }

  // Handles pb:dropdown:updateOptions - replace options when event.detail.dropdownId matches.
  // detail: { dropdownId, options: [{ id, label, value }], clearSelection?: boolean }
  handleUpdateOptionsEvent(event) {
    if (this.isDisabled) return;
    const targetId = event.detail?.dropdownId;
    if (!targetId || this.element.id !== targetId) return;

    const options = event.detail?.options;
    if (!Array.isArray(options)) return;

    const clearSelection = event.detail?.clearSelection !== false;
    this.replaceOptions(options, { clearSelection });
  }

  // Handles options_event_type events - replace options when detail.options is present.
  handleOptionsEventType(event) {
    if (this.isDisabled) return;
    const targetId = event.detail?.dropdownId;
    if (targetId != null && this.element.id !== targetId) return;

    const options = event.detail?.options;
    if (!Array.isArray(options)) return;

    const clearSelection = event.detail?.clearSelection !== false;
    this.replaceOptions(options, { clearSelection });
  }

  // Handles pb:dropdown:clear - clear this dropdown when event.detail.dropdownId matches.
  handleClearEvent(event) {
    if (this.isDisabled) return;
    const targetId = event.detail?.dropdownId;
    if (targetId && this.element.id === targetId) {
      this.clearSelection();
    }
  }

  // Handles custom_event_type events (e.g. turbo:submit-end) - clear when detail.dropdownId matches or is omitted.
  handleCustomClearEvent(event) {
    if (this.isDisabled) return;
    const targetId = event.detail?.dropdownId;
    if (targetId == null || this.element.id === targetId) {
      this.clearSelection();
    }
  }

  // Handles pb:dropdown:select - set dropdown selection by option id(s).
  // Single: detail: { dropdownId, optionId }. Multi: detail: { dropdownId, optionIds: ['id1', 'id2'] }.
  handleSelectEvent(event) {
    if (this.isDisabled) return;
    const targetId = event.detail?.dropdownId;
    if (!targetId || this.element.id !== targetId) return;

    const optionId = event.detail?.optionId;
    const optionIds = event.detail?.optionIds;
    if (optionId != null) {
      this.setSelectionByOptionId(optionId);
    } else if (Array.isArray(optionIds)) {
      this.setSelectionByOptionIds(optionIds);
    }
  }

  // Set single-select dropdown to the option with the given id. No-op if id not found.
  setSelectionByOptionId(optionId) {
    if (this.isMultiSelect) return;
    const hiddenInput = this.baseInput;
    const optionEls = Array.from(this.queryAllOptions());
    const selectedOption = optionEls.find((opt) => {
      try {
        return String(JSON.parse(opt.dataset.dropdownOptionLabel).id) === String(optionId);
      } catch {
        return false;
      }
    });
    if (!selectedOption) return;
    if (selectedOption.dataset.dropdownOptionDisabled === "true") return;

    optionEls.forEach((opt) => opt.classList.remove("pb_dropdown_option_selected"));
    selectedOption.classList.add("pb_dropdown_option_selected");
    if (hiddenInput) hiddenInput.value = optionId;
    this.selectedOptionJson = selectedOption.dataset.dropdownOptionLabel;
    const optionData = JSON.parse(this.selectedOptionJson);
    const customDisplayElement = this.element.querySelector(
      '[data-dropdown-trigger-custom-display]',
    );
    if (customDisplayElement) {
      this.setTriggerElementText("");
      customDisplayElement.style.display = "block";
      customDisplayElement.style.paddingRight = "8px";
    } else {
      this.setTriggerElementText(optionData.label);
    }
    if (this.searchInput) {
      this.searchInput.value = optionData.label;
    }

    if (optionData.formatted_start_date && optionData.formatted_end_date) {
      const startDateId = this.element.dataset.startDateId;
      const endDateId = this.element.dataset.endDateId;
      const controlsStartId = this.element.dataset.controlsStartId;
      const controlsEndId = this.element.dataset.controlsEndId;
      if (startDateId) {
        const startDateInput = document.getElementById(startDateId);
        if (startDateInput) startDateInput.value = optionData.formatted_start_date;
      }
      if (endDateId) {
        const endDateInput = document.getElementById(endDateId);
        if (endDateInput) endDateInput.value = optionData.formatted_end_date;
      }
      const syncDatePickers = () => {
        if (controlsStartId) {
          const startPicker = document.querySelector(`#${CSS.escape(controlsStartId)}`)?._flatpickr;
          setQuickPickDateOnPicker(startPicker, optionData.formatted_start_date, "start");
        }
        if (controlsEndId) {
          const endPicker = document.querySelector(`#${CSS.escape(controlsEndId)}`)?._flatpickr;
          setQuickPickDateOnPicker(endPicker, optionData.formatted_end_date, "end");
        }
      };
      syncDatePickers();
      setTimeout(syncDatePickers, 100);
      setTimeout(syncDatePickers, 300);
    }

    this.updateClearButton();
    this.emitSelectionChange();
  }

  // Set multi-select dropdown to the options with the given ids. Invalid ids are skipped.
  setSelectionByOptionIds(optionIds) {
    if (!this.isMultiSelect || !optionIds.length) return;
    const optionEls = Array.from(this.queryAllOptions());
    this.selectedOptions.clear();
    optionEls.forEach((opt) => {
      opt.classList.remove("pb_dropdown_option_selected");
      opt.style.display = "";
    });

    optionIds.forEach((id) => {
      const opt = optionEls.find((o) => {
        try {
          return String(JSON.parse(o.dataset.dropdownOptionLabel).id) === String(id);
        } catch {
          return false;
        }
      });
      if (opt) {
        if (opt.dataset.dropdownOptionDisabled === "true") return;

        const raw = opt.dataset.dropdownOptionLabel;
        this.selectedOptions.add(raw);
        opt.style.display = "none";
      }
    });

    this.updatePills();
    this.updateClearButton();
    this.adjustDropdownHeight();
    this.syncHiddenInputs();
    this.emitSelectionChange();
  }
  // ----- End External events handling section -----

  isClickOutside(event) {
    const label = event.target.closest(LABEL_SELECTOR);
    if (label && this.element.contains(label)) return false;
    const customTrigger = this.customTrigger;
    if (customTrigger) {
      const clickInTrigger = customTrigger.contains(event.target);
      const clickInContainer = this.target?.contains(event.target);
      return !clickInTrigger && !clickInContainer;
    } else {
      const triggerElement = this.trigger;
      const containerElement = this.target;

      const isOutsideTrigger = triggerElement
        ? !triggerElement.contains(event.target)
        : true;
      const isOutsideContainer = containerElement
        ? !containerElement.contains(event.target)
        : true;

      return isOutsideTrigger && isOutsideContainer;
    }
  }

  emitSelectionChange() {
    let detail;

    if (this.isMultiSelect) {
      detail = Array.from(this.selectedOptions).map(JSON.parse);
    } else {
      detail = this.baseInput.value && this.selectedOptionJson
        ? JSON.parse(this.selectedOptionJson)
        : null;
    }
    this.element.setAttribute("data-option-selected", JSON.stringify(detail));
    this.element.dispatchEvent(
      new CustomEvent("pb:dropdown:selected", {
        detail,
        bubbles: true,
      }),
    );
  }

  onOptionSelected(value, selectedOption) {
    if (!this.isMultiSelect) this.selectedOptionJson = value;
    if (this.isAsync) this.cancelAsyncSearch();
    const triggerElement = this.element.querySelector(DROPDOWN_TRIGGER_DISPLAY);
    const customDisplayElement = this.element.querySelector(
      '[data-dropdown-trigger-custom-display]',
    );

    if (triggerElement) {
      if (!this.isMultiSelect) {
        const selectedLabel = JSON.parse(value).label;
        triggerElement.textContent = selectedLabel;
        if (!this.isAsync) this.emitSelectionChange();

        // Handle quickpick variant: populate start/end date hidden inputs
        const optionData = JSON.parse(value);
        const startDateId = this.element.dataset.startDateId;
        const endDateId = this.element.dataset.endDateId;
        const controlsStartId = this.element.dataset.controlsStartId;
        const controlsEndId = this.element.dataset.controlsEndId;

        if (optionData.formatted_start_date && optionData.formatted_end_date) {
          // Populate date inputs when option has date fields
          if (startDateId) {
            const startDateInput = document.getElementById(startDateId);
            if (startDateInput)
              startDateInput.value = optionData.formatted_start_date;
          }

          if (endDateId) {
            const endDateInput = document.getElementById(endDateId);
            if (endDateInput)
              endDateInput.value = optionData.formatted_end_date;
          }

          // Sync with DatePickers if controlsStartId/controlsEndId are present
          if (controlsStartId) {
            const startPicker = document.querySelector(
              `#${controlsStartId}`,
            )?._flatpickr;
            if (startPicker) {
              setQuickPickDateOnPicker(
                startPicker,
                optionData.formatted_start_date,
                "start",
              );
            }
          }

          if (controlsEndId) {
            const endPicker = document.querySelector(
              `#${controlsEndId}`,
            )?._flatpickr;
            if (endPicker) {
              setQuickPickDateOnPicker(
                endPicker,
                optionData.formatted_end_date,
                "end",
              );
            }
          }
        } else if (startDateId || endDateId) {
          // Clear date inputs when option doesn't have date fields (e.g., blank selection)
          if (startDateId) {
            const startDateInput = document.getElementById(startDateId);
            if (startDateInput) startDateInput.value = "";
          }

          if (endDateId) {
            const endDateInput = document.getElementById(endDateId);
            if (endDateInput) endDateInput.value = "";
          }

          // Clear DatePickers as well
          if (controlsStartId) {
            const startPicker = document.querySelector(
              `#${controlsStartId}`,
            )?._flatpickr;
            if (startPicker) {
              startPicker.clear();
            }
          }

          if (controlsEndId) {
            const endPicker = document.querySelector(
              `#${controlsEndId}`,
            )?._flatpickr;
            if (endPicker) {
              endPicker.clear();
            }
          }
        }
      }
      if (customDisplayElement) {
        triggerElement.textContent = "";
        customDisplayElement.style.display = "block";
        customDisplayElement.style.paddingRight = "8px";
      }
    }

    const autocompleteInput = this.element.querySelector(SEARCH_INPUT_SELECTOR);
    if (autocompleteInput && !this.isMultiSelect) {
      autocompleteInput.value = JSON.parse(value).label;
      if (!this.isAsync) this.emitSelectionChange();
    }

    const shouldCloseOnOptionSelect =
      this.closeOnClick === "any" || this.closeOnClick === "inside";
    if (shouldCloseOnOptionSelect && this.target.classList.contains("open")) {
      this.hideElement(this.target);
      this.updateArrowDisplay(false);
    }

    const options = this.queryAllOptions();
    if (this.isMultiSelect) {
      if (!this.isAsync) this.emitSelectionChange();
      Array.from(this.selectedOptions).map((option) => {
        if (this.sameOption(option, selectedOption.dataset.dropdownOptionLabel)) {
          selectedOption.style.display = "none";
        }
      });
      this.showMultiSelectEmptyMenu();
      this.adjustDropdownHeight();
      this.syncHiddenInputs();
    } else {
      options.forEach((option) => {
        option.classList.remove("pb_dropdown_option_selected");
      });
      selectedOption.classList.add("pb_dropdown_option_selected");
      if (this.isAsync) this.resetOptionFilterForSelectedLabel();
    }
    this.updateClearButton();
    if (this.isAsync) this.emitSelectionChange();
  }

  showElement(elem) {
    if (this.isDisabled) return;
    let retryTerm = null;
    if (this.isAsync && this.asyncSearchInterrupted) {
      this.asyncSearchInterrupted = false;
      retryTerm = this.searchInput?.value || this.searchBar?.value || "";
    }
    this.showAsyncEmptyState();
    this.showMultiSelectEmptyMenu();
    if (!this.canOpenMenu && retryTerm == null) return;
    if (!this.canOpenMenu) {
      this.searchAsync(retryTerm);
      return;
    }

    // When autocomplete is showing the selected label, sync which options stay visible.
    this.resetOptionFilterForSelectedLabel();
    
    this.ensureFloatingPortalConfig();
    if (this.useMenuPortal) {
      this.mountPortalMenu(elem);
    }

    elem.classList.remove("close");
    elem.classList.add("open");

    // Drop a previous explicit height so hidden results do not leave empty space.
    elem.style.height = "auto";
    elem.offsetHeight;
    const shouldConstrain = elem.classList.contains("constrain_height");
    if (shouldConstrain) {
      // Calculate height respecting max-height constraint (18em)
      const fontSize = parseFloat(getComputedStyle(elem).fontSize) || 16;
      const maxHeight = fontSize * 18; // matches SCSS max-height: 18em
      const scrollHeight = elem.scrollHeight;
      const height = Math.min(scrollHeight, maxHeight);
      elem.style.height = height + "px";
    } else {
      elem.style.height = elem.scrollHeight + "px";
    }

    if (this.useMenuPortal) {
      this.applyPortalPosition();
      window.removeEventListener("resize", this.boundApplyPortalPosition);
      window.addEventListener("resize", this.boundApplyPortalPosition);
      if (this._unsubscribePortalReposition) {
        this._unsubscribePortalReposition();
        this._unsubscribePortalReposition = null;
      }
      this._unsubscribePortalReposition = subscribeFloatingKitReposition(
        this.boundApplyPortalPosition,
      );
      window.requestAnimationFrame(() => this.applyPortalPosition());
    } else {
      this.adjustDropdownPosition(elem);
    }
    if (retryTerm != null) this.searchAsync(retryTerm);
  }

  hideElement(elem) {
    if (this.isAsync) {
      const pending = this.asyncSearchTimer != null || this.element.getAttribute("aria-busy") === "true";
      if (pending) {
        const restore = this.asyncResultsCleared;
        this.cancelAsyncSearch();
        if (restore) this.restoreSettledAsyncResults();
        this.asyncResultsCleared = false;
        const query = this.searchInput?.value || this.searchBar?.value || "";
        this.asyncSearchInterrupted = query.length > 0;
      }
      this.updateArrowDisplay(false);
    }
    elem.style.height = elem.scrollHeight + "px";
    window.setTimeout(() => {
      if (this.useMenuPortal) {
        this.unmountPortalMenu();
      }
      elem.classList.add("close");
      elem.classList.remove("open");
      this.resetFocus();
    }, 0);
  }

  resetFocus() {
    if (this.keyboardHandler) {
      this.keyboardHandler.focusedOptionIndex = -1;
      const options = this.queryAllOptions();
      options.forEach((option) =>
        option.classList.remove("pb_dropdown_option_focused"),
      );
    }
  }

  toggleElement(elem) {
    if (this.isDisabled) return;
    const isOpen = toggleVisibility({
      isVisible: elem.classList.contains("open"),
      onHide: () => this.hideElement(elem),
      onShow: () => this.showElement(elem),
    });

    this.updateArrowDisplay(isOpen);
  }

  updateArrowDisplay(isOpen) {
    setArrowVisibility({
      rootElement: this.element,
      downSelector: DOWN_ARROW_SELECTOR,
      upSelector: UP_ARROW_SELECTOR,
      showDownArrow: !(isOpen && this.canOpenMenu),
    });
  }

  handleFormValidation() {
    const hiddenInput = this.baseInput;

    hiddenInput.addEventListener(
      "invalid",
      function (event) {
        if (hiddenInput.hasAttribute("required") && hiddenInput.value === "") {
          event.preventDefault();
          hiddenInput.closest(".dropdown_wrapper").classList.add("error");
        }
      },
      true,
    );
  }

  clearFormValidation(input) {
    // PbFormValidation may have setCustomValidity from data-message; clear it
    // before checkValidity so a filled value can pass constraint validation.
    input.setCustomValidity("");

    if (this.isMultiSelect) {
      if (this.selectedOptions.size > 0) {
        const dropdownWrapperElement = input.closest(".dropdown_wrapper");
        dropdownWrapperElement.classList.remove("error");
        const errorLabelElement = dropdownWrapperElement.querySelector(
          ".pb_body_kit_negative",
        );
        if (errorLabelElement) {
          errorLabelElement.remove();
        }
        return;
      }
    }

    if (input.checkValidity()) {
      const dropdownWrapperElement = input.closest(".dropdown_wrapper");
      dropdownWrapperElement.classList.remove("error");

      const errorLabelElement = dropdownWrapperElement.querySelector(
        ".pb_body_kit_negative",
      );
      if (errorLabelElement) {
        errorLabelElement.remove();
      }
    }
  }

  setDefaultValue() {
    const hiddenInput = this.baseInput;
    const optionEls = Array.from(
      this.queryAllOptions(),
    );
    // Async defaults may not appear in the first remote result page.
    if (this.isAsync && this.element.dataset.pbDropdownDefaultValue) {
      const defaults = JSON.parse(this.element.dataset.pbDropdownDefaultValue);
      (this.isMultiSelect ? defaults : [defaults]).forEach((option) => {
        if (!optionEls.some((opt) => String(JSON.parse(opt.dataset.dropdownOptionLabel).id) === String(option.id))) {
          optionEls.push(this.buildOptionElement(option));
        }
      });
    }
    const defaultValue = hiddenInput.dataset.defaultValue || "";
    if (!defaultValue) return;

    if (this.isMultiSelect) {
      const ids = defaultValue.split(",");
      ids.forEach((id) => {
        const selectedOption = optionEls.find((opt) => {
          try {
            return String(JSON.parse(opt.dataset.dropdownOptionLabel).id) === String(id);
          } catch {
            return false;
          }
        });
        if (!selectedOption) {
          console.warn(`Dropdown default ID ${id} not found`);
          return;
        }

        const raw = selectedOption.dataset.dropdownOptionLabel;
        this.selectedOptions.add(raw);

        selectedOption.style.display = "none";
      });

      this.updatePills();
      this.updateClearButton();
      this.adjustDropdownHeight();
      this.syncHiddenInputs();
    } else {
      hiddenInput.value = defaultValue;
      const selectedOption = optionEls.find((opt) => {
        try {
          return (
            String(JSON.parse(opt.dataset.dropdownOptionLabel).id) === defaultValue
          );
        } catch {
          return false;
        }
      });
      if (!selectedOption) return;

      selectedOption.classList.add("pb_dropdown_option_selected");
      this.selectedOptionJson = selectedOption.dataset.dropdownOptionLabel;
      const optionData = JSON.parse(this.selectedOptionJson);
      this.setTriggerElementText(optionData.label);

      // Autocomplete trigger has no [data-dropdown-trigger-display]; set the input value instead
      const autocompleteInput =
        this.searchInput || this.element.querySelector(SEARCH_INPUT_SELECTOR);
      if (autocompleteInput) {
        autocompleteInput.value = optionData.label;
      }

      // Handle quickpick variant: populate start/end date hidden inputs and sync DatePickers
      if (optionData.formatted_start_date && optionData.formatted_end_date) {
        const startDateId = this.element.dataset.startDateId;
        const endDateId = this.element.dataset.endDateId;
        const controlsStartId = this.element.dataset.controlsStartId;
        const controlsEndId = this.element.dataset.controlsEndId;

        if (startDateId) {
          const startDateInput = document.getElementById(startDateId);
          if (startDateInput)
            startDateInput.value = optionData.formatted_start_date;
        }

        if (endDateId) {
          const endDateInput = document.getElementById(endDateId);
          if (endDateInput) endDateInput.value = optionData.formatted_end_date;
        }

        // Sync with DatePickers - retry with delays to ensure DatePickers are initialized
        const syncDatePickers = () => {
          if (controlsStartId) {
            const startPicker = document.querySelector(
              `#${controlsStartId}`,
            )?._flatpickr;
            if (startPicker) {
              setQuickPickDateOnPicker(
                startPicker,
                optionData.formatted_start_date,
                "start",
              );
            }
          }

          if (controlsEndId) {
            const endPicker = document.querySelector(
              `#${controlsEndId}`,
            )?._flatpickr;
            if (endPicker) {
              setQuickPickDateOnPicker(
                endPicker,
                optionData.formatted_end_date,
                "end",
              );
            }
          }
        };

        // Try immediately
        syncDatePickers();

        // Retry after short delay in case DatePickers aren't ready yet
        setTimeout(syncDatePickers, 100);
        setTimeout(syncDatePickers, 300);
      }
    }
  }

  handleFormReset() {
    this.formElement = this.element.closest("form");
    if (!this.formElement) return;

    this.formResetHandler = (event) => {
      if (!this.isAsync) {
        this.resetDropdownValue();
        return;
      }
      const handler = this.formResetHandler;
      // Run after native inputs reset, and honor canceled resets or disconnects.
      Promise.resolve().then(() => {
        if (!event.defaultPrevented && this.formResetHandler === handler) {
          this.clearSelection("reset");
        }
      });
    };
    this.formElement.addEventListener("reset", this.formResetHandler);
  }

  resetDropdownValue({ preserveOptions = false } = {}) {
    if (this.allowOptionCreation) this.removeCreationOption();
    this.selectedOptionJson = null;
    if (this.isAsync) {
      if (!preserveOptions) this.clearAsyncResults();
      if (this.searchBar) this.searchBar.value = "";
    }
    const hiddenInput = this.baseInput;
    const options = this.queryAllOptions();
    options.forEach((option) => {
      option.classList.remove("pb_dropdown_option_selected");
      option.style.display = "";
    });

    hiddenInput.value = "";

    const defaultPlaceholder = this.placeholder;
    this.setTriggerElementText(defaultPlaceholder?.dataset.dropdownPlaceholder || "");

    if (this.searchInput) {
      this.searchInput.value = "";
      if (this.target.classList.contains("open")) {
        const el = this.target;
        el.style.height = "auto";
        requestAnimationFrame(() => {
          const newHeight = el.scrollHeight + "px";
          el.offsetHeight; // force reflow
          el.style.height = newHeight;
        });
      }
    }
    if (this.isMultiSelect) {
      this.selectedOptions.clear();
      this.updatePills();
      this.updateClearButton();
      this.syncHiddenInputs();
    }
  }

  setTriggerElementText(text) {
    const triggerElement = this.element.querySelector(DROPDOWN_TRIGGER_DISPLAY);
    if (triggerElement) {
      triggerElement.textContent = text;
    }
  }

  getPillOptionId(optionData) {
    return String(optionData.id ?? optionData.value ?? optionData.label ?? "");
  }

  reorderSelectedByIds(orderedIds) {
    const byId = new Map(
      Array.from(this.selectedOptions).map((raw) => {
        const data = JSON.parse(raw);
        return [this.getPillOptionId(data), raw];
      }),
    );
    const next = orderedIds.map((id) => byId.get(String(id))).filter(Boolean);
    if (next.length !== this.selectedOptions.size) return;

    this.selectedOptions = new Set(next);
    this.updatePills();
    this.updateClearButton();
    this.syncHiddenInputs();
    this.emitReorderChange();
    this.emitSelectionChange();
  }

  emitReorderChange() {
    const detail = Array.from(this.selectedOptions).map(JSON.parse);
    this.element.dispatchEvent(
      new CustomEvent("pb:dropdown:reordered", {
        detail,
        bubbles: true,
      }),
    );
  }

  moveSelectedOption(fromIndex, direction) {
    const items = Array.from(this.selectedOptions);
    const toIndex = direction === "left" ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= items.length) return;

    const [moved] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, moved);
    this.selectedOptions = new Set(items);
    this.updatePills();
    this.updateClearButton();
    this.syncHiddenInputs();
    this.emitReorderChange();
    this.emitSelectionChange();

    requestAnimationFrame(() => {
      const wrapper = this.element.querySelector("[data-dropdown-pills-wrapper]");
      const pills = wrapper?.querySelectorAll(".pb_form_pill_kit, .pb_form_pill_kit_icon");
      pills?.[toIndex]?.focus();
    });
  }

  updatePills() {
    if (!this.isMultiSelect) return;

    const wrapper = this.element.querySelector('[data-dropdown-pills-wrapper]');
    const placeholder = this.element.querySelector(
      '[data-dropdown-trigger-display-multi-select]',
    );
    if (!wrapper) return;

    if (typeof this.pillReorderCleanup === "function") {
      this.pillReorderCleanup();
      this.pillReorderCleanup = null;
    }

    wrapper.innerHTML = "";
    // Show or hide the placeholder based on selected options
    if (placeholder) {
      if (this.selectedOptions.size > 0) {
        placeholder.style.display = "none";
      } else {
        placeholder.style.display = "";
      }
    }

    const reorderEnabled = this.enablePillReorder && !this.isDisabled;
    let pillsContainer = wrapper;
    if (reorderEnabled) {
      pillsContainer = document.createElement("div");
      pillsContainer.className = "pb_dropdown_draggable_pills pb_draggable_container";
      pillsContainer.setAttribute("data-pb-drag-container", "dropdown-pills");
      wrapper.appendChild(pillsContainer);
    }

    const selectedList = Array.from(this.selectedOptions);
    const gripSize = this.formPillProps.size === "small" ? "xs" : "sm";
    // Path from @powerhome/playbook-icons grip-dots-vertical (same source as React Icon / Typeahead)
    const gripSvg = `<svg class="pb_custom_icon svg-inline--fa svg_${gripSize} svg_fw pb_form_pill_icon pb_dropdown_pill_grip_icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 31 25" aria-hidden="true"><path fill="currentColor" d="M12.9043 6.35547C12.0605 6.35547 11.4043 5.69922 11.4043 4.85547C11.4043 4.05859 12.0605 3.35547 12.9043 3.35547C13.7012 3.35547 14.4043 4.05859 14.4043 4.85547C14.4043 5.69922 13.7012 6.35547 12.9043 6.35547ZM12.9043 13.8555C12.0605 13.8555 11.4043 13.1992 11.4043 12.3555C11.4043 11.5586 12.0605 10.8555 12.9043 10.8555C13.7012 10.8555 14.4043 11.5586 14.4043 12.3555C14.4043 13.1992 13.7012 13.8555 12.9043 13.8555ZM14.4043 19.8555C14.4043 20.6992 13.7012 21.3555 12.9043 21.3555C12.0605 21.3555 11.4043 20.6992 11.4043 19.8555C11.4043 19.0586 12.0605 18.3555 12.9043 18.3555C13.7012 18.3555 14.4043 19.0586 14.4043 19.8555ZM18.9043 6.35547C18.0605 6.35547 17.4043 5.69922 17.4043 4.85547C17.4043 4.05859 18.0605 3.35547 18.9043 3.35547C19.7012 3.35547 20.4043 4.05859 20.4043 4.85547C20.4043 5.69922 19.7012 6.35547 18.9043 6.35547ZM20.4043 12.3555C20.4043 13.1992 19.7012 13.8555 18.9043 13.8555C18.0605 13.8555 17.4043 13.1992 17.4043 12.3555C17.4043 11.5586 18.0605 10.8555 18.9043 10.8555C19.7012 10.8555 20.4043 11.5586 20.4043 12.3555ZM18.9043 21.3555C18.0605 21.3555 17.4043 20.6992 17.4043 19.8555C17.4043 19.0586 18.0605 18.3555 18.9043 18.3555C19.7012 18.3555 20.4043 19.0586 20.4043 19.8555C20.4043 20.6992 19.7012 21.3555 18.9043 21.3555Z"/></svg>`;

    selectedList.forEach((option, index) => {
      const optionData = JSON.parse(option);
      const optionId = this.getPillOptionId(optionData);
      const color = this.formPillProps.color || "primary";
      const showInPillGrip = reorderEnabled && !this.externalDragHandle;
      const pill = document.createElement("div");
      pill.classList.add(
        showInPillGrip ? "pb_form_pill_kit_icon" : "pb_form_pill_kit",
        `pb_form_pill_${color}`,
        "pb_form_pill_none",
        "mr_xs",
      );
      if (this.formPillProps.size === "small") {
        pill.classList.add("pb_form_pill_small");
      }
      pill.tabIndex = 0;
      pill.dataset.pillId = optionData.id;
      const label = optionData.label || "";
      const displayLabel = this.showPillIndex ? `${index + 1}. ${label}` : label;
      if (reorderEnabled) {
        pill.setAttribute(
          "aria-label",
          `${label}, position ${index + 1} of ${selectedList.length}, press Left or Right Arrow keys to reorder`,
        );
      }

      if (showInPillGrip) {
        pill.insertAdjacentHTML("beforeend", gripSvg);
      }

      const innerDiv = document.createElement("h3");
      innerDiv.className = "pb_title_kit pb_title_4 pb_form_pill_text";
      innerDiv.textContent = displayLabel;
      pill.appendChild(innerDiv);

      const closeIcon = document.createElement("div");
      closeIcon.className = "pb_form_pill_close";
      closeIcon.innerHTML = `<svg class="pb_custom_icon svg-inline--fa svg_${
        this.formPillProps.size === "small" ? "xs" : "sm"
      } svg_fw" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 31 25"><path fill="currentColor" d="M23.0762 6.77734L17.4512 12.4023L23.0293 17.9805C23.498 18.4023 23.498 19.1055 23.0293 19.5273C22.6074 19.9961 21.9043 19.9961 21.4824 19.5273L15.8574 13.9492L10.2793 19.5273C9.85742 19.9961 9.1543 19.9961 8.73242 19.5273C8.26367 19.1055 8.26367 18.4023 8.73242 17.9336L14.3105 12.3555L8.73242 6.77734C8.26367 6.35547 8.26367 5.65234 8.73242 5.18359C9.1543 4.76172 9.85742 4.76172 10.3262 5.18359L15.9043 10.8086L21.4824 5.23047C21.9043 4.76172 22.6074 4.76172 23.0762 5.23047C23.498 5.65234 23.498 6.35547 23.0762 6.77734Z"/></svg>`;
      pill.appendChild(closeIcon);

      closeIcon.addEventListener("click", (e) => {
        e.stopPropagation();
        this.selectedOptions.delete(option);

        const optEl = Array.from(this.queryAllOptions()).find((opt) =>
          this.sameOption(opt.dataset.dropdownOptionLabel, option),
        );
        if (this.isAsync && this.selectedOptions.size === 0 && !this.searchInput?.value && !this.searchBar?.value) {
          // Removing the last pill drops the earlier search, as React does.
          const wasOpen = this.target.classList.contains("open");
          this.forgetSettledAsyncResults();
          this.cancelAsyncSearch();
          this.getOptionsParent()?.replaceChildren();
          this.removeNoOptionsMessage();
          this.resetFocus();
          if (wasOpen) {
            this.showAsyncEmptyState();
            this.adjustDropdownHeight();
          }
        } else if (optEl) {
          optEl.style.display = "";
          if (this.target.classList.contains("open")) {
            this.showElement(this.target);
          }
        }

        this.updatePills();
        this.updateClearButton();
        this.emitSelectionChange();
        this.syncHiddenInputs();
      });

      if (reorderEnabled) {
        pill.addEventListener("keydown", (event) => {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            event.stopPropagation();
            this.moveSelectedOption(index, event.key === "ArrowLeft" ? "left" : "right");
          } else if (event.key === "Backspace" || event.key === "Delete") {
            event.preventDefault();
            event.stopPropagation();
            closeIcon.click();
          }
        });

        const item = document.createElement("span");
        item.className = "pb_draggable_item";
        item.setAttribute("data-pb-drag-id", optionId);
        item.setAttribute("draggable", "false");

        if (this.externalDragHandle) {
          const handleWrap = document.createElement("span");
          handleWrap.className = "pb_dropdown_pill_with_handle";
          const handle = document.createElement("span");
          handle.className = "pb_draggable_handle pb_dropdown_external_drag_handle";
          handle.setAttribute("aria-hidden", "true");
          handle.innerHTML = gripSvg;
          handleWrap.appendChild(handle);
          handleWrap.appendChild(pill);
          item.appendChild(handleWrap);
        } else {
          item.appendChild(pill);
        }

        pillsContainer.appendChild(item);
      } else {
        pillsContainer.appendChild(pill);
      }
    });

    if (reorderEnabled && selectedList.length > 0) {
      const stopTriggerToggle = (event) => {
        if (event.target.closest(".pb_form_pill_close")) return;
        event.stopPropagation();
      };
      pillsContainer.addEventListener("click", stopTriggerToggle);
      pillsContainer.addEventListener("mousedown", stopTriggerToggle);

      const reorderCleanup = bindPillReorder({
        container: pillsContainer,
        getOrderedIds: () => Array.from(this.selectedOptions).map((raw) => (
          this.getPillOptionId(JSON.parse(raw))
        )),
        onReorder: (orderedIds) => this.reorderSelectedByIds(orderedIds),
        externalDragHandle: this.externalDragHandle,
      });

      this.pillReorderCleanup = () => {
        pillsContainer.removeEventListener("click", stopTriggerToggle);
        pillsContainer.removeEventListener("mousedown", stopTriggerToggle);
        reorderCleanup();
      };
    }
  }

  clearSelection(reason = "clear", { preserveOptions = false } = {}) {
    if (this.isDisabled && reason !== "reset") return;
    if (this.allowOptionCreation) this.removeCreationOption();
    if (this.isMultiSelect) {
      this.selectedOptions.clear();
      this.queryAllOptions().forEach((opt) => {
        opt.style.display = "";
      });
      if (this.target.classList.contains("open")) {
        this.showElement(this.target);
      }
    }
    const customDisplay = this.element.querySelector(
      '[data-dropdown-trigger-custom-display]',
    );
    if (customDisplay) {
      customDisplay.style.display = "none";
    }

    // Clear quickpick hidden inputs
    const startDateId = this.element.dataset.startDateId;
    const endDateId = this.element.dataset.endDateId;
    const controlsStartId = this.element.dataset.controlsStartId;
    const controlsEndId = this.element.dataset.controlsEndId;

    if (startDateId) {
      const startDateInput = document.getElementById(startDateId);
      if (startDateInput) startDateInput.value = "";
    }
    if (endDateId) {
      const endDateInput = document.getElementById(endDateId);
      if (endDateInput) endDateInput.value = "";
    }

    // Clear linked DatePickers if controlsStartId/controlsEndId are present
    if (controlsStartId) {
      const startPicker = document.querySelector(
        `#${controlsStartId}`,
      )?._flatpickr;
      if (startPicker) {
        startPicker.clear();
      }
    }

    if (controlsEndId) {
      const endPicker = document.querySelector(`#${controlsEndId}`)?._flatpickr;
      if (endPicker) {
        endPicker.clear();
      }
    }

    this.resetDropdownValue({ preserveOptions });
    this.updatePills();
    this.updateClearButton();
    this.syncHiddenInputs();
    this.emitSelectionChange();
    if (this.isAsync) this.emitInputChange("", reason);
  }

  // Method for DatePicker sync - only clears the dropdown, not the DatePickers
  clearSelected() {
    // Only clear if this is a single-select quickpick variant
    if (
      this.element.dataset.pbDropdownVariant !== "quickpick" ||
      this.isMultiSelect
    ) {
      return;
    }

    const customDisplay = this.element.querySelector(
      '[data-dropdown-trigger-custom-display]',
    );
    if (customDisplay) {
      customDisplay.style.display = "none";
    }

    // Clear quickpick hidden inputs only (not the DatePickers)
    const startDateId = this.element.dataset.startDateId;
    const endDateId = this.element.dataset.endDateId;

    if (startDateId) {
      const startDateInput = document.getElementById(startDateId);
      if (startDateInput) startDateInput.value = "";
    }
    if (endDateId) {
      const endDateInput = document.getElementById(endDateId);
      if (endDateInput) endDateInput.value = "";
    }

    this.resetDropdownValue();
    this.updateClearButton();
    this.emitSelectionChange();
  }

  syncHiddenInputs() {
    if (!this.isMultiSelect) return;
    this.element
      .querySelectorAll('input[data-generated="true"]')
      .forEach((n) => n.remove());

    const baseInput = this.baseInput;
    if (!baseInput) return;
    // for multi_select, for each selectedOption, create a hidden input
    const name = this.baseInputName;
    let previousInput = baseInput;
    this.selectedOptions.forEach((raw) => {
      const optionData = JSON.parse(raw);
      // Use id if available, otherwise fall back to value
      const id = optionData.id ?? optionData.value;
      const inp = document.createElement("input");
      inp.type = "hidden";
      if (name) inp.name = name;
      inp.value = id;
      inp.disabled = this.isDisabled;
      inp.dataset.generated = "true";
      previousInput.insertAdjacentElement("afterend", inp);
      previousInput = inp;
    });

    // Generated inputs own submission; the base input validates empty selections.
    baseInput.value = "";
    if (this.selectedOptions.size > 0) {
      baseInput.removeAttribute("name");
      baseInput.removeAttribute("required");
      baseInput.setCustomValidity("");
    } else {
      if (name) baseInput.setAttribute("name", name);
      if (this.wasOriginallyRequired) baseInput.setAttribute("required", "");
    }
  }

  handleBackspaceClear() {
    // Async input handling clears a single selection synchronously for all edit methods.
    if (this.isAsync && !this.isMultiSelect) return;
    if (!this.isMultiSelect) {
      this.selectedOptionJson = null;
      this.queryAllOptions().forEach((opt) => {
        opt.classList.remove("pb_dropdown_option_selected");
        opt.style.display = "";
        this.adjustDropdownHeight();
      });

      const hiddenInput = this.baseInput;
      if (hiddenInput) hiddenInput.value = "";

      const placeholder = this.placeholder;
      if (placeholder)
        this.setTriggerElementText(placeholder.dataset.dropdownPlaceholder);
    }
    if (this.isMultiSelect) {
      this.queryAllOptions().forEach((opt) => {
        const optValue = opt.dataset.dropdownOptionLabel;
        if (
          this.selectedOptions.size > 0 &&
          Array.from(this.selectedOptions).some((raw) =>
            this.sameOption(raw, optValue),
          )
        ) {
          opt.style.display = "none";
        } else {
          opt.style.display = "";
        }
        this.adjustDropdownHeight();
      });
    }
  }
}
